/**
 * EhrTasksSection — one bucket of the EHR Tasks report: its title and count, then a
 * read-only, sortable table (no Review buttons: the work is done on EHR Tasks).
 *
 * Rows arrive oldest received first, which is the default order. A bucket over
 * SHOW_FIRST rows shows the first SHOW_FIRST with a "Show all" toggle (plan 9.4).
 */

import { useMemo, useState } from 'react';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';
import { SortIcon } from '../../../tasks/ehr/components/EhrTaskTable.jsx';
import { compareValues } from '../../../../utils/sortRows.js';
import { formatEhrCell } from '../ehrTasksColumns.js';

const SHOW_FIRST = 100;

const EhrTasksSection = ({ bucket, rows, tzOffset }) => {
    const [sortColumn, setSortColumn] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');
    const [showAll, setShowAll] = useState(false);

    const sorted = useMemo(() => {
        if (!sortColumn) return rows;
        /* Dates and the received time sort on their stored ISO text, which orders correctly. */
        return [...rows].sort((a, b) => compareValues(a[sortColumn], b[sortColumn], sortOrder));
    }, [rows, sortColumn, sortOrder]);

    const visible = showAll ? sorted : sorted.slice(0, SHOW_FIRST);

    const handleSort = (key) => {
        if (sortColumn === key) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(key);
            setSortOrder('asc');
        }
    };

    return (
        <section className="pp-section etr-bucket" id={`etr-bucket-${bucket.key}`} aria-labelledby={`etr-bucket-title-${bucket.key}`}>
            <div className="pp-section__header">
                <h3 className="pp-section__title" id={`etr-bucket-title-${bucket.key}`}>
                    {bucket.title}
                    <span className="etr-bucket__count">{rows.length}</span>
                </h3>
            </div>
            {rows.length === 0 ? (
                <p className="etr-bucket__empty">No {bucket.noun} waiting for review.</p>
            ) : (
                <>
                    <div className="dp-table-wrap">
                        <table className="dp-table">
                            <thead className="dp-thead">
                                <tr>
                                    {bucket.columns.map((col) => (
                                        <th key={col.key} scope="col" className="dp-th" aria-sort={ariaSort(sortColumn, sortOrder, col.key)}>
                                            <div className="dp-th__content">
                                                <button
                                                    type="button"
                                                    className="dp-th__sort-btn"
                                                    onClick={() => handleSort(col.key)}
                                                    aria-label={`Sort by ${col.label}`}
                                                >
                                                    {col.label}
                                                    <SortIcon active={sortColumn === col.key} order={sortOrder} />
                                                </button>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="dp-tbody">
                                {visible.map((row, idx) => (
                                    <tr key={`${row.task_type}-${row.received_utc}-${row.name}-${idx}`} className="dp-tr">
                                        {bucket.columns.map((col) => {
                                            const text = formatEhrCell(row, col, tzOffset);
                                            return (
                                                <td key={col.key} className="dp-td">
                                                    {text
                                                        ? <span className="dp-cell-text">{text}</span>
                                                        : <span className="dp-cell-text--muted">&mdash;</span>}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {sorted.length > SHOW_FIRST ? (
                        <div className="etr-bucket__more">
                            <span>
                                {showAll
                                    ? `Showing all ${sorted.length} ${bucket.noun}.`
                                    : `Showing ${SHOW_FIRST} of ${sorted.length} ${bucket.noun}.`}
                            </span>
                            <button type="button" className="rp-btn rp-btn--secondary" onClick={() => setShowAll((v) => !v)}>
                                {showAll ? `Show first ${SHOW_FIRST}` : `Show all ${sorted.length}`}
                            </button>
                        </div>
                    ) : null}
                </>
            )}
        </section>
    );
};

export default EhrTasksSection;
